import { useEffect, useState, FunctionComponent } from "react";
import { Modal, Text, Input, Grid } from "@nextui-org/react";
import CostProfileSpec, {
  ConvertToTitle,
} from "@pages/pricingConfig/components/costProfileSpec";
import GroupMultiSelect from "@pages/pricingConfig/components/groupMultiSelect";
import { IoTV2API } from "@services/index";
import {
  editableCostProfileToJsonObj,
  handleError,
  handleSuccess,
  renameKey,
  sleepThenUpdate,
} from "@utils/pricingConfig";
import { useSnackbar } from "notistack";
import CustomButton from "@pages/pricingConfig/components/customButton";
import TextAreaModal from "@pages/pricingConfig/components/textAreaModal";

function CostProfileGridInput({
  labelLeft,
  labelRight,
  label,
  defaultValue,
  icon,
  readOnly = false,
  disabled = false,
  onChange,
  type = "number",
}: any) {
  return (
    <Grid>
      <Input
        required
        width={"200px"}
        //clearable
        bordered
        fullWidth
        size="lg"
        label={label}
        initialValue={defaultValue}
        value={defaultValue}
        onChange={onChange}
        contentLeft={icon}
        labelRight={labelRight}
        labelLeft={labelLeft}
        readOnly={readOnly}
        disabled={disabled}
        type={type}
      />
    </Grid>
  );
}

export default function EditableCostProfile({
  isCreate = false,
  costProfile,
  setCostProfile,
  visible,
  setVisible,
}: any) {
  const [groups, setGroups] = useState<any[]>([]);
  const [cp, setCp] = useState<any>({ ...costProfile });
  const [jsonImportVisible, setJsonImportVisible] = useState(false);
  const [json, setJson] = useState("");
  const setJsonFromCP = () => {
    setJson(JSON.stringify(editableCostProfileToJsonObj(cp), null, 2));
  };
  useEffect(() => {
    IoTV2API.get("groups").then((response) => {
      const groups = response.data;
      renameKey(groups, "id", "value");
      renameKey(groups, "name", "label");
      setGroups(groups);
    });
  }, []);
  useEffect(() => {
    setJsonFromCP();
  }, [cp]);
  useEffect(() => {
    setCp({ ...costProfile });
  }, [costProfile]);
  const { enqueueSnackbar } = useSnackbar();
  const closeHandler = () => {
    setVisible(false);
  };
  return (
    <>
      <div background-color="black">
        <TextAreaModal
          visible={jsonImportVisible}
          setVisible={setJsonImportVisible}
          title={"Import / Edit JSON"}
          text={json}
          setText={setJson}
          onCancel={() => {
            setJsonFromCP();
          }}
          helperText={
            "This is the JSON representation of the cost profile. You can edit it directly or import it from another cost profile."
          }
          onConfirm={(text: string) => {
            try {
              const json = JSON.parse(text);
              setCp({ ...cp, ...json, id: cp.id, version: cp.version });
              enqueueSnackbar("Imported successfully", { variant: "success" });
              setJsonImportVisible(false);
            } catch (e) {
              enqueueSnackbar("Invalid JSON", { variant: "error" });
            }
          }}
          label={"JSON"}
          placeholder={
            "You can copy the json of any other cost profile and paste it here"
          }
        />
        <Modal
          preventClose
          aria-labelledby="modal-title"
          open={visible}
          onClose={closeHandler}
          width={"60vw"}
        >
          <Modal.Header>
            <Text id="modal-title" b size={20}>
              {!isCreate ? (
                <>{`Editing - ${costProfile.name}`}</>
              ) : (
                "Creating Cost Profile"
              )}
            </Text>
          </Modal.Header>
          <Modal.Body>
            <GroupMultiSelect
              onChange={(value: string[]) => {
                setCp({ ...cp, group_ids: value });
              }}
              data={groups}
              defaultValue={cp.group_ids}
            />
            <Grid.Container gap={2} css={{ padding: 0 }} justify={"flex-start"}>
              {Object.keys(CostProfileSpec).map((key: string) => {
                const spec = CostProfileSpec[key];
                const title = spec.title ?? ConvertToTitle(key);
                if (spec.readOnly && isCreate) {
                  return <div key={key} />;
                }
                return (
                  <CostProfileGridInput
                    key={key}
                    label={`${title}`}
                    defaultValue={cp[key]}
                    type={spec.type}
                    onChange={(e: any) => {
                      setCp({
                        ...cp,
                        [key]:
                          spec.type === "number"
                            ? parseFloat(e.target.value)
                            : e.target.value,
                      });
                    }}
                    icon={spec.icon}
                    disabled={spec.readOnly}
                    labelRight={
                      <span
                        style={{
                          width: "40px",
                          textAlign: "right",
                        }}
                      >{`${spec.prefix}${spec.suffix}`}</span>
                    }
                  />
                );
              })}
            </Grid.Container>
          </Modal.Body>
          <Modal.Footer>
            <CustomButton
              cbuttonType={"transparent"}
              auto
              onClick={closeHandler}
            >
              Cancel
            </CustomButton>
            <CustomButton
              bold={false}
              auto
              onClick={() => {
                setJsonImportVisible(true);
              }}
            >
              Import / Edit JSON
            </CustomButton>
            <CustomButton
              bold={false}
              auto
              onClick={() => {
                if (!isCreate) {
                  if (JSON.stringify(costProfile) === JSON.stringify(cp)) {
                    enqueueSnackbar("No changes detected", {
                      variant: "default",
                    });
                    return;
                  }
                }
                if (isCreate) {
                  IoTV2API.post("cost-profile", cp)
                    .then((response) => {
                      handleSuccess(enqueueSnackbar);
                      setVisible(false);
                      setCostProfile(response.data);
                      sleepThenUpdate().then();
                    })
                    .catch((error) => {
                      handleError(error, enqueueSnackbar);
                    });
                } else {
                  IoTV2API.patch(`cost-profile/${costProfile.id}`, cp)
                    .then((response) => {
                      handleSuccess(enqueueSnackbar);
                      setCostProfile(response.data);
                      sleepThenUpdate().then();
                    })
                    .catch((error) => {
                      handleError(error, enqueueSnackbar);
                    });
                }
              }}
            >
              &nbsp;&nbsp;&nbsp;Save (Doesn't Apply)&nbsp;&nbsp;&nbsp;
            </CustomButton>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}
